import { getTargetLanguageFromPair } from './languagePairs'
import {
  canonicalizeLanguageKey,
  canonicalizeTranslationProgressMap,
} from './languageIdentity'

export type LibraryTranslationStatus =
  | 'complete'
  | 'in_progress'
  | 'not_started'
  | 'unavailable'

export type LibraryProgressState = 'ready' | 'refreshing' | 'unavailable'

export type LibraryLanguageStatusBook = {
  language?: string | null
  translation_pairs?: unknown[] | null
  manual_completed_languages?: string[] | null
  translation_progress?: Record<string, unknown> | null
  progress_state?: LibraryProgressState | null
  flagStatus?: 'ready' | 'loading' | 'error' | null
}

const canonical = (value: string | null | undefined) => canonicalizeLanguageKey(value || '')

export const getBookTargetLanguages = (book: LibraryLanguageStatusBook): string[] => {
  const seen = new Set<string>()
  const targets: string[] = []

  ;(book.translation_pairs || []).forEach(pair => {
    const target = getTargetLanguageFromPair(pair)
    const key = canonical(target)
    if (!target || !key || seen.has(key)) return
    seen.add(key)
    targets.push(target)
  })

  return targets
}

export const resolveLibraryTranslationStatus = (
  book: LibraryLanguageStatusBook,
  targetLanguage: string,
  manualCompletedLanguages: string[] = book.manual_completed_languages || [],
): LibraryTranslationStatus | null => {
  const targetKey = canonical(targetLanguage)
  if (!targetKey || targetKey === canonical(book.language)) return null

  if (manualCompletedLanguages.some(language => canonical(language) === targetKey)) {
    return 'complete'
  }

  const hasPair = getBookTargetLanguages(book).some(language => canonical(language) === targetKey)
  if (!hasPair) return 'not_started'

  if (book.progress_state === 'refreshing' || book.progress_state === 'unavailable' || book.flagStatus === 'error') {
    return 'unavailable'
  }

  const progress = canonicalizeTranslationProgressMap(book.translation_progress as any)
  const entry = Object.entries(progress || {}).find(([language]) => canonical(language) === targetKey)?.[1]
  if (entry?.status === 'complete') return 'complete'
  if (entry?.status === 'in_progress') return 'in_progress'
  if (entry?.status === 'not_started') return 'not_started'

  return 'unavailable'
}

export const bookMatchesLanguageStatus = (
  book: LibraryLanguageStatusBook,
  targetLanguage: string | null,
  status: LibraryTranslationStatus | null,
  manualCompletedLanguages?: string[],
): boolean => {
  if (!targetLanguage) return true

  // Selecting only a language means "show books with translation output in this
  // language". `translation_pairs` also contains planned, zero-progress targets,
  // so pair presence alone is not evidence that a translated book exists.
  if (!status) {
    const resolved = resolveLibraryTranslationStatus(book, targetLanguage, manualCompletedLanguages)
    return resolved === 'complete' || resolved === 'in_progress'
  }

  const resolved = resolveLibraryTranslationStatus(book, targetLanguage, manualCompletedLanguages)
  if (resolved === null) return false
  return resolved === status
}
